// ============================================================================
// OBT F2-A — Taslak kaydetme + yayın önizleme + yayınlama orkestrasyonu
// ============================================================================
// F2 Backend Enabler sonrası GERÇEK sözleşme:
//   • Sorular ATOMİK olarak PUT /campaigns/{id}/questions ile kaydedilir
//     (yarım set oluşmaz). Tek tek POST YAPILMAZ.
//   • Yayın öncesi POST /campaigns/{id}/publication-preview salt-okunur
//     doğrulama verir (ok, question_count, target_count, issues, next_version).
//   • Kampanya `version` bir ZİNCİRDİR: create/PATCH ve her soru PUT'u sürümü
//     artırır; bir sonraki yazımda güncel sürüm `expected_*` olarak gönderilir.
//     Uyuşmazlık → 409; kullanıcıya güncel taslağı yeniden yükleme sunulur.
//
// Akış (talimat §5/§7):
//   Taslak kaydet : meta (create/PATCH) → sorular (atomik PUT)
//   Yayınla       : meta → sorular → preview → (kullanıcı onayı) → publish
//
// Çift gönderim `submitting` bayrağı + çağıranın buton disable'ı ile engellenir.
// ============================================================================

import { useCallback, useRef, useState } from 'react';
import {
  createCampaign,
  updateCampaign,
  replaceCampaignQuestions,
  publicationPreview,
  publishCampaign,
} from './campaignApi';
import { normalizeObtError, OBT_ERROR_KIND } from '../obtErrors';

function metaKey(basics) {
  return JSON.stringify({
    title: (basics.title || '').trim(),
    description: (basics.description || '').trim(),
    topic: (basics.topic || '').trim(),
    responseDueAt: basics.responseDueAt || '',
  });
}

/**
 * @param {object} initial
 * @param {string|null} initial.campaignId  Mevcut taslak düzenleniyorsa kimliği
 * @param {number|null} initial.version      Mevcut taslağın sürümü
 */
export function useCampaignSubmit(initial = {}) {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const idRef = useRef(initial.campaignId || null);
  const versionRef = useRef(
    typeof initial.version === 'number' ? initial.version : null
  );
  // Son sunucuya yazılmış meta imzası — değişmediyse gereksiz PATCH atmayız
  // (gereksiz sürüm artışını önler). Mevcut taslak yüklenirken çağıran `reset`
  // ile doldurur.
  const lastMetaRef = useRef(initial.metaKey || null);

  /** 409/stale çakışması mı? */
  const isConflict = (normalized) =>
    normalized?.kind === OBT_ERROR_KIND.CONFLICT || normalized?.status === 409;

  /**
   * Yeniden yükleme sonrası (409 çözümü) veya taslak açılışında iç durumu tazeler.
   */
  const reset = useCallback(({ campaignId, version, basics } = {}) => {
    if (campaignId !== undefined) idRef.current = campaignId;
    if (typeof version === 'number') versionRef.current = version;
    if (basics) lastMetaRef.current = metaKey(basics);
    setError(null);
  }, []);

  /** Meta'yı oluştur/güncelle; güncel sürümü döndürür. Değişmemişse PATCH atmaz. */
  const saveMeta = useCallback(async (basics, signal) => {
    const payload = {
      title: (basics.title || '').trim(),
      description: basics.description?.trim() || undefined,
      topic: basics.topic?.trim() || undefined,
      responseDueAt: basics.responseDueAt || undefined,
    };
    if (!idRef.current) {
      const created = await createCampaign(payload, { signal });
      idRef.current = created.campaign_id;
      versionRef.current =
        typeof created.version === 'number' ? created.version : 0;
      lastMetaRef.current = metaKey(basics);
      return idRef.current;
    }
    // Mevcut taslak: yalnız meta DEĞİŞTİYSE PATCH (talimat §5).
    // Owner enabler sonrası `description` GET ile geri OKUNUR; artık açık biçimde
    // gönderilir (boşaltılırsa gerçek boş değer → temizlenir). Değişmemişse
    // metaKey aynıdır → PATCH atılmaz → mevcut açıklama korunur.
    if (lastMetaRef.current !== metaKey(basics)) {
      const updated = await updateCampaign(
        idRef.current,
        {
          title: payload.title,
          description: (basics.description ?? '').trim(), // boş → temizlenir
          topic: payload.topic, // undefined → gönderilmez → korunur
          responseDueAt: payload.responseDueAt ?? null,
          expectedVersion:
            typeof versionRef.current === 'number' ? versionRef.current : undefined,
        },
        { signal }
      );
      versionRef.current =
        typeof updated.version === 'number' ? updated.version : versionRef.current;
      lastMetaRef.current = metaKey(basics);
    }
    return idRef.current;
  }, []);

  /** Soru setini ATOMİK PUT ile kaydeder; yeni sürümü state'e alır. */
  const saveQuestions = useCallback(async (questions, signal) => {
    const items = (questions || []).map((q, i) => ({ ...q, display_order: i }));
    const out = await replaceCampaignQuestions(
      idRef.current,
      {
        expectedVersion:
          typeof versionRef.current === 'number' ? versionRef.current : undefined,
        questions: items,
      },
      { signal }
    );
    if (typeof out?.campaign_version === 'number') {
      versionRef.current = out.campaign_version;
    }
    return out;
  }, []);

  /** Meta + sorular (atomik). Ortak yardımcı. */
  const saveAll = useCallback(
    async (basics, questions, signal) => {
      await saveMeta(basics, signal);
      await saveQuestions(questions, signal);
    },
    [saveMeta, saveQuestions]
  );

  const withSubmit = useCallback(async (fn) => {
    setSubmitting(true);
    setError(null);
    try {
      return await fn();
    } catch (e) {
      const normalized = normalizeObtError(e);
      setError(normalized);
      return { ok: false, error: normalized, conflict: isConflict(normalized) };
    } finally {
      setSubmitting(false);
    }
  }, []);

  /** Taslak kaydet: meta + sorular atomik (talimat §5). */
  const saveDraft = useCallback(
    ({ basics, questions }) =>
      withSubmit(async () => {
        await saveAll(basics, questions);
        return {
          ok: true,
          campaignId: idRef.current,
          version: versionRef.current,
        };
      }),
    [withSubmit, saveAll]
  );

  /** Yayın öncesi: meta + sorular kaydet, sonra backend preview (talimat §6/§7). */
  const runPreview = useCallback(
    ({ basics, questions, targetIds }) =>
      withSubmit(async () => {
        await saveAll(basics, questions);
        const preview = await publicationPreview(idRef.current, targetIds);
        return {
          ok: true,
          preview,
          campaignId: idRef.current,
          version: versionRef.current,
        };
      }),
    [withSubmit, saveAll]
  );

  /** Kullanıcı onayı sonrası gerçek yayın. Preview çalışmadan çağrılmamalıdır. */
  const confirmPublish = useCallback(
    ({ targetIds }) =>
      withSubmit(async () => {
        if (!idRef.current) {
          throw new Error('Yayın için bilgi talebi kaydı yok.');
        }
        const result = await publishCampaign(idRef.current, targetIds);
        return { ok: true, result, campaignId: idRef.current };
      }),
    [withSubmit]
  );

  return {
    submitting,
    error,
    saveDraft,
    runPreview,
    confirmPublish,
    reset,
    campaignId: idRef.current,
    getVersion: () => versionRef.current,
  };
}
